import { TableFooter, TableRow, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useTranslation } from 'react-i18next'
import { Column, WorkDay } from '../../../store/types'
import { sumProjectPerMonth } from '../../../store/utils'
import { StyledBoxInStickyCell, StyledFooterCell } from '../../../styles/WorklogTableStyles'
import DecimalHourDurationTooltip from '../../Tooltip/DecimalHourDurationTooltip'
import { ColumnLayout } from './WorklogTable'
import { useMemo } from 'react'
import { roundUpToNearestMinute } from '../../../store/utils/worklogUtils'

type WorklogTableFooterProps = {
  worklog: WorkDay[] | undefined
  columns: Column[]
  vacationId: string
  columnLayout: ColumnLayout
}

const WorklogTableFooter = ({ worklog, columns, vacationId, columnLayout }: WorklogTableFooterProps) => {
  const { t } = useTranslation()
  const { col1Width, col2Width, col3Width, paddingLeft: multiColumnPaddingLeft } = columnLayout

  const sum = useMemo(() => {
    return columns.reduce((accumulator, object) => {
      return accumulator + object.sum
    }, 0)
  }, [columns])

  const vacation = useMemo(() => {
    return sumProjectPerMonth(worklog, vacationId, true)
  }, [vacationId, worklog])

  const completeSum = useMemo(() => {
    return roundUpToNearestMinute(sum + vacation)
  }, [sum, vacation])

  return (
    <TableFooter
      sx={{
        height: '50px',
        bgcolor: 'background.elevation1',
        width: '100%',
        left: 0,
        bottom: 0,
        zIndex: 50,
        position: 'sticky',
      }}
    >
      <TableRow>
        <StyledFooterCell sx={{ left: 0, position: 'sticky', p: 0 }} colSpan={3}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: 'fit-content',
              pl: `${multiColumnPaddingLeft}px`,
            }}
          >
            <Box sx={{ width: col1Width }}></Box>
            <StyledBoxInStickyCell sx={{ borderRight: '0', width: col2Width }}>
              <Typography variant="body2">
                {worklog?.length} {t('page.worklog.table.days')}
              </Typography>
            </StyledBoxInStickyCell>
            <StyledBoxInStickyCell
              sx={{
                justifyContent: 'center',
                width: col3Width,
                borderRight: 0,
              }}
            >
              <DecimalHourDurationTooltip hours={completeSum} arrow>
                <Typography variant="body2" fontWeight={700}>
                  {completeSum === 0 ? '-' : completeSum}
                </Typography>
              </DecimalHourDurationTooltip>
            </StyledBoxInStickyCell>
          </Box>
        </StyledFooterCell>

        {vacation !== 0 && (
          <StyledFooterCell align="center" sx={{ paddingX: '16px' }}>
            <Typography variant="body2">{vacation}</Typography>
          </StyledFooterCell>
        )}

        {columns?.length === 0 ? (
          <StyledFooterCell align="center">
            <Typography variant="body2">-</Typography>
          </StyledFooterCell>
        ) : (
          columns?.map(column => {
            if (column?.projectId === vacationId) return null
            const rounded = roundUpToNearestMinute(column?.sum)
            return (
              <StyledFooterCell align="center" key={`footer-column-${column.projectId}-${column.id}`}>
                <DecimalHourDurationTooltip hours={rounded} arrow>
                  <Typography variant="body2">{rounded}</Typography>
                </DecimalHourDurationTooltip>
              </StyledFooterCell>
            )
          })
        )}
      </TableRow>
    </TableFooter>
  )
}

export default WorklogTableFooter
